import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../supabaseClient';

const FONT = "'DM Sans', system-ui, sans-serif";

function utcDate(ts) {
  if (!ts) return new Date();
  if (typeof ts === 'string' && !ts.endsWith('Z') && !ts.includes('+')) return new Date(ts + 'Z');
  return new Date(ts);
}

function parseMsg(row) {
  try {
    const p = JSON.parse(row.description);
    if (p._t === 'm') return { id: row.id, from: p.f, email: p.e || null, text: p.t, at: row.created_at };
  } catch {}
  return null;
}

function emailToName(email) {
  if (!email) return '?';
  const local = email.split('@')[0];
  return local.replace(/[._-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function emailToInitial(email) {
  if (!email) return '?';
  return email[0].toUpperCase();
}

export default function Messenger({ clientId, role, senderEmail, dark = true }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const endRef = useRef(null);
  const lastCountRef = useRef(0);

  const load = useCallback(async () => {
    if (!clientId) return;
    const { data } = await supabase
      .from('tasks')
      .select('*')
      .eq('client_id', clientId)
      .order('created_at', { ascending: true });
    const msgs = (data || []).map(parseMsg).filter(Boolean);
    setMessages(msgs);
    if (!open && msgs.length > lastCountRef.current) {
      setUnread(u => u + (msgs.length - lastCountRef.current));
    }
    lastCountRef.current = msgs.length;
  }, [clientId, open]);

  useEffect(() => { load(); const iv = setInterval(load, 4000); return () => clearInterval(iv); }, [load]);

  useEffect(() => {
    if (open) { setUnread(0); endRef.current?.scrollIntoView({ behavior: 'smooth' }); }
  }, [open, messages.length]);

  async function send() {
    if (!text.trim() || sending) return;
    setSending(true);
    await supabase.from('tasks').insert({
      client_id: clientId,
      description: JSON.stringify({ _t: 'm', f: role, e: senderEmail || null, t: text.trim() }),
      created_at: new Date().toISOString(),
    });
    setText('');
    setSending(false);
    load();
  }

  const border = 'var(--border)';
  const textPrimary = 'var(--text)';
  const textSecondary = 'var(--text-dim)';
  const surface = 'var(--bg-btn)';
  const inputBg = 'var(--bg-input)';

  const myEmail = senderEmail?.toLowerCase();

  return (
    <>
      {/* Floating button */}
      <button onClick={() => setOpen(o => !o)} style={{
        position: 'fixed', bottom: 24, right: 24, zIndex: 900,
        width: 56, height: 56, borderRadius: 28, border: 'none',
        background: 'var(--accent)', color: '#fff', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 4px 16px rgba(230,48,48,0.4), 0 2px 6px rgba(0,0,0,0.2)',
        transition: 'transform 0.2s cubic-bezier(0.25,0.46,0.45,0.94), box-shadow 0.2s',
        transform: open ? 'scale(0.9)' : 'scale(1)',
      }}>
        {open ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        )}
        {unread > 0 && !open && (
          <span style={{
            position: 'absolute', top: -4, right: -4,
            width: 22, height: 22, borderRadius: 11,
            background: 'var(--danger)', color: '#fff', fontSize: 11, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '2px solid var(--bg)',
          }}>{unread > 9 ? '9+' : unread}</span>
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div style={{
          position: 'fixed', bottom: 96, right: 24, zIndex: 901,
          width: 380, maxWidth: 'calc(100vw - 48px)',
          height: 520, maxHeight: 'calc(100vh - 140px)',
          background: dark ? 'rgba(20,20,20,0.95)' : 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(24px) saturate(180%)',
          WebkitBackdropFilter: 'blur(24px) saturate(180%)',
          borderRadius: 20, border: `1px solid ${border}`,
          boxShadow: '0 8px 40px rgba(0,0,0,0.3), 0 2px 8px rgba(0,0,0,0.15)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{
            padding: '16px 20px', borderBottom: `1px solid ${border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
          }}>
            <div>
              <p style={{ fontSize: 15, fontWeight: 600, color: textPrimary, margin: 0, fontFamily: FONT }}>Group Chat</p>
              <p style={{ fontSize: 12, color: textSecondary, margin: '2px 0 0 0', fontFamily: FONT }}>
                Admins & team members
              </p>
            </div>
            <button onClick={() => setOpen(false)} style={{
              background: surface, border: 'none', borderRadius: 8, width: 32, height: 32,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: textSecondary,
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1, overflowY: 'auto', padding: '16px 16px 8px',
            display: 'flex', flexDirection: 'column', gap: 2,
          }}>
            {messages.length === 0 && (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <p style={{ fontSize: 13, color: textSecondary, textAlign: 'center', fontFamily: FONT }}>
                  No messages yet. Say hello!
                </p>
              </div>
            )}
            {messages.map((m, i) => {
              const isMine = m.email?.toLowerCase() === myEmail;
              const showDate = i === 0 || utcDate(m.at).toDateString() !== utcDate(messages[i - 1].at).toDateString();
              const prevSameSender = i > 0 && messages[i - 1].email === m.email;
              const isAdmin = m.from === 'admin';

              return (
                <div key={m.id}>
                  {showDate && (
                    <p style={{ fontSize: 11, color: textSecondary, textAlign: 'center', margin: '10px 0', fontFamily: FONT }}>
                      {utcDate(m.at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </p>
                  )}
                  {/* Sender name — shown when sender changes */}
                  {!isMine && !prevSameSender && (
                    <p style={{
                      fontSize: 11, fontWeight: 600, margin: '8px 0 3px 36px', fontFamily: FONT,
                      color: isAdmin ? 'var(--accent)' : 'var(--text-muted)',
                    }}>
                      {emailToName(m.email)}{isAdmin ? ' · Admin' : ''}
                    </p>
                  )}
                  <div style={{
                    display: 'flex', alignItems: 'flex-end', gap: 8,
                    justifyContent: isMine ? 'flex-end' : 'flex-start',
                    marginBottom: 2,
                  }}>
                    {/* Avatar for others */}
                    {!isMine && (
                      <div style={{
                        width: 28, height: 28, borderRadius: 14, flexShrink: 0,
                        background: isAdmin ? 'color-mix(in srgb, var(--accent) 12%, transparent)' : 'var(--bg-btn)',
                        color: isAdmin ? 'var(--accent)' : 'var(--text-muted)',
                        fontSize: 12, fontWeight: 700, fontFamily: FONT,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        visibility: prevSameSender ? 'hidden' : 'visible',
                      }}>
                        {emailToInitial(m.email)}
                      </div>
                    )}
                    <div style={{
                      maxWidth: '75%', padding: '10px 14px',
                      borderRadius: isMine ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                      background: isMine ? 'var(--accent)' : surface,
                      color: isMine ? '#fff' : textPrimary,
                      fontSize: 14, lineHeight: 1.45, fontFamily: FONT,
                      wordBreak: 'break-word',
                    }}>
                      {m.text}
                    </div>
                  </div>
                  <p style={{
                    fontSize: 10, color: textSecondary, margin: '1px 0 0 0',
                    textAlign: isMine ? 'right' : 'left', paddingLeft: isMine ? 0 : 36, paddingRight: isMine ? 4 : 0,
                    fontFamily: FONT,
                  }}>
                    {utcDate(m.at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  </p>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>

          {/* Input */}
          <div style={{
            padding: '12px 16px', borderTop: `1px solid ${border}`,
            display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0,
          }}>
            <input
              style={{
                flex: 1, padding: '10px 14px', background: inputBg, border: `1px solid ${border}`,
                borderRadius: 20, fontSize: 14, fontFamily: FONT, color: textPrimary, outline: 'none',
              }}
              placeholder="Type a message…"
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            />
            <button onClick={send} disabled={sending || !text.trim()} style={{
              width: 36, height: 36, borderRadius: 18, border: 'none',
              background: text.trim() ? 'var(--accent)' : surface,
              color: text.trim() ? '#fff' : textSecondary,
              cursor: text.trim() ? 'pointer' : 'default',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.15s ease', flexShrink: 0,
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
